import { Box } from '@ltvco/refresh-lib/theme';
import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { StyledConversationList } from 'components/DeepSearch/components/ChatHistory/components';
import { ConversationItem } from 'components/DeepSearch/components/ChatHistory/ConversationItem';
import { DeepSearchContext } from 'components/DeepSearch/context';

export const DeepSearchConversationList = ({
  collapsed,
  onConversationClick,
}: {
  collapsed: boolean;
  onConversationClick?: () => void;
}) => {
  const { conversations, activeConversationId, selectConversation } =
    useContext(DeepSearchContext);
  const navigate = useNavigate();
  const prevTitlesRef = useRef<Record<string, string>>({});
  const [animatingIds, setAnimatingIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    const changed = new Set<string>();
    conversations.forEach((c) => {
      const prev = prevTitlesRef.current[c.id];
      if (prev !== undefined && prev !== c.title) {
        changed.add(c.id);
      }
      prevTitlesRef.current[c.id] = c.title;
    });

    if (changed.size === 0) {
      return;
    }

    setAnimatingIds(
      (prev) => new Set([...Array.from(prev), ...Array.from(changed)])
    );
    const timer = setTimeout(() => {
      setAnimatingIds((prev) => {
        const next = new Set(prev);
        changed.forEach((id) => next.delete(id));
        return next;
      });
    }, 850);
    return () => clearTimeout(timer);
  }, [conversations]);

  const handleConversationClick = (id: string) => {
    selectConversation(id);
    navigate(`/deepsearch/chat/conversation/${id}`);
    onConversationClick?.();
  };

  return (
    <StyledConversationList collapsed={collapsed}>
      {/* Fill the list width so items can't hug their content — the parent list
          uses alignItems: flex-start, so without this the items grow to the
          longest title and clip instead of truncating with an ellipsis. */}
      <Box sx={{ width: '100%' }}>
        {conversations.map((conversation) => {
          const isActive = conversation.id === activeConversationId;
          return (
            <ConversationItem
              key={conversation.id}
              title={conversation.title}
              isActive={isActive}
              animate={animatingIds.has(conversation.id)}
              onClick={() => handleConversationClick(conversation.id)}
            />
          );
        })}
      </Box>
    </StyledConversationList>
  );
};
